import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { leasingScoringApi } from '@/api/leasing-scoring.api';
import type {
  TLeasingRequestHistory,
  TLeasingScoringRequest
} from '@/pages/LeasingScoringRequest/types/request.types';
import {
  EFlashLogSource,
  LEASING_LOG_SOURCES
} from '@/shared/constants/leasing';
import { getNewestValues, keysToCamelCase } from '@/shared/services/tools';
import type { TLeasingFile } from '@/shared/types/files.types';
import type {
  IScoringInfoApi,
  TScoringInfo
} from '@/shared/types/scoring-info.types';

import type { TRootState } from '.';

export const getLeasingRequestInfo = createAsyncThunk(
  'leasingScoringRequests/getLeasingRequestInfo',
  async (id: string, { getState }) => {
    const { userInfo } = getState() as TRootState;
    const response = await leasingScoringApi.getRequestInfo(id);

    let scoringInfo: IScoringInfoApi | null = null;
    if (
      response.Decisions?.[0]?.uuid &&
      (userInfo?.userAccesses.global.isUnderwriter ||
        userInfo?.userAccesses.global.isAdmin)
    ) {
      try {
        scoringInfo = await leasingScoringApi.getScoringInfo(
          response.Decisions[0].uuid
        );
        if (scoringInfo.risk_zone) {
          scoringInfo.risk_zone = scoringInfo.risk_zone?.toLowerCase() as
            | 'red'
            | 'yellow'
            | 'green';
        }
      } catch (err) {
        scoringInfo = null;
        console.error(err);
      }
    }

    return {
      requestInfo: response,
      scoringInfo
    };
  }
);

export const getLeasingRequestLogs = createAsyncThunk(
  'leasingScoringRequests/getLeasingRequestLogs',
  async (id: string) => {
    const response = await leasingScoringApi.getRequestLogs(id);
    const filteredResponse = response.filter((log) =>
      LEASING_LOG_SOURCES.includes(log.source as EFlashLogSource)
    );
    const newestValues = getNewestValues(filteredResponse, 'source', 'date');
    return keysToCamelCase(newestValues).reverse();
  }
);

interface ILeasingScoringRequestsSlice {
  requestInfo: TLeasingScoringRequest | null;
  scoringInfo: TScoringInfo | null;
  clientDecisionDate: string | null;
  requestLogs: TLeasingRequestHistory[] | null;
  isLoadingLogs: boolean;
}

const initialState: ILeasingScoringRequestsSlice = {
  requestInfo: null,
  scoringInfo: null,
  clientDecisionDate: null,
  requestLogs: null,
  isLoadingLogs: false
};

const leasingScoringRequests = createSlice({
  name: 'leasingScoringRequests',
  initialState,
  reducers: {
    resetLeasingRequestInfo: (state) => {
      state.requestInfo = null;
      state.clientDecisionDate = null;
      state.requestLogs = null;
      state.isLoadingLogs = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLeasingRequestInfo.fulfilled, (state, action) => {
        const { Files, ...rest } = action.payload.requestInfo;
        state.requestInfo = {
          ...keysToCamelCase(rest),
          files: Files?.reduce(
            (acc, file) => {
              if (file.file_type?.code)
                acc[file.file_type.code] = keysToCamelCase(file);
              return acc;
            },
            {} as Record<string, TLeasingFile>
          )
        };
        state.scoringInfo = keysToCamelCase(action.payload.scoringInfo);
      })
      .addCase(getLeasingRequestLogs.fulfilled, (state, action) => {
        state.requestLogs = action.payload;
      })
      .addCase(getLeasingRequestLogs.pending, (state) => {
        state.isLoadingLogs = true;
      })
      .addCase(getLeasingRequestLogs.rejected, (state) => {
        state.isLoadingLogs = false;
      });
  }
});

export const { resetLeasingRequestInfo } = leasingScoringRequests.actions;
export default leasingScoringRequests;
